import React, { useCallback, useMemo, useState } from 'react'
import { MenuItem, Select, Stack, Typography } from '@mui/material'

import { Button } from '../../../../interaxDesignSystemComponents'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { LeaderScopeGroup } from './leaderScopeHelpers'
import { leaderScopeGroupPickerSx, leaderScopeMutedTextSx } from './leaderScopeStyles'

export type LeaderScopeQuickAddProps = {
  labelKey: string
  groups: LeaderScopeGroup[]
  selectedUserIds: string[]
  onAdd: (userIds: string[]) => void
}

/** Ticking a whole team or sprint at once. It only ever adds people to the list being built. */
export const LeaderScopeQuickAdd: React.FC<LeaderScopeQuickAddProps> = ({
  labelKey,
  groups,
  selectedUserIds,
  onAdd
}) => {
  const { t } = useTranslation()
  const [chosenGroupId, setChosenGroupId] = useState('')

  const chosenGroup = groups.find((group) => group.id === chosenGroupId)

  const newUserIds = useMemo(
    () => (chosenGroup?.userIds ?? []).filter((userId) => !selectedUserIds.includes(userId)),
    [chosenGroup, selectedUserIds]
  )

  const addChosenGroup = useCallback(() => {
    onAdd(newUserIds)
    setChosenGroupId('')
  }, [onAdd, newUserIds])

  if (groups.length === 0) return null

  const pickerLabel = t(labelKey)
  const hasChosenGroup = chosenGroup !== undefined
  const chosenGroupLabel = chosenGroup?.label ?? pickerLabel

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row', md: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', sm: 'center', md: 'center' }}
    >
      <Select
        size="small"
        displayEmpty
        value={chosenGroupId}
        onChange={(event) => setChosenGroupId(event.target.value)}
        renderValue={() => chosenGroupLabel}
        inputProps={{ 'aria-label': pickerLabel }}
        sx={leaderScopeGroupPickerSx}
      >
        {groups.map((group) => (
          <MenuItem key={group.id} value={group.id}>
            {group.label}
          </MenuItem>
        ))}
      </Select>

      {hasChosenGroup && (
        <Typography sx={leaderScopeMutedTextSx}>
          {t('org.leaderScope.willAdd', { count: newUserIds.length })}
        </Typography>
      )}

      <Button variant="secondary" size="small" disabled={newUserIds.length === 0} onClick={addChosenGroup}>
        {t('org.leaderScope.add')}
      </Button>
    </Stack>
  )
}
